// Usage Dashboard Types

import type { AllLicensesResponse } from '~/billing/services/billingService'

// ============================================================================
// BILLABLE FLOW PLAN DETECTION — SINGLE SOURCE OF TRUTH (FRONTEND)
//
// The backend determines billable flow status based on license name:
//   - enterprise-migrate, enterprise-migrate-accelerate, enterprise-migrate-refactor → billable flows
//   - enterprise-migrate-assess → credits
//   - all other plans → credits
//
// The frontend should use this helper everywhere instead of inline computation.
// ============================================================================

/**
 * Determine if the current billing account is on a billable flow plan.
 *
 * Checks multiple sources for maximum reliability:
 * 1. API usage response (`credits.is_billable_flow_plan`) — available to all users
 * 2. allLicenses context (`allLicenses.is_billable_flow_plan`) — admin users
 * 3. allLicenses hide_credits flag — legacy indicator
 *
 * All sources are driven by the same backend logic (license name check).
 */
export function getIsBillableFlowPlan(
  credits?: Pick<CreditsData, 'is_billable_flow_plan'> | null,
  allLicenses?: Pick<AllLicensesResponse, 'is_billable_flow_plan' | 'hide_credits'> | null
): boolean {
  return !!(
    credits?.is_billable_flow_plan ||
    allLicenses?.is_billable_flow_plan ||
    allLicenses?.hide_credits
  )
}

export interface RecentMessage {
  title: string
  message_count: number
}

export interface LeaderboardEntry {
  id: string
  name: string
  messages: number
  tasks: number
  time_saved: string
  credit_used: number
  billable_flows: number
  recent_messages?: RecentMessage[]
}

export interface ChartDataPoint {
  label: string
  messages_exchanged: number
  tasks_run: number
}

export interface CategoryStat {
  count: number
  percentage: number
}

export interface UsageOverviewData {
  total_messages_exchanged: number
  tasks_run: number
  categories: {
    coding: CategoryStat
    testing: CategoryStat
    documentation: CategoryStat
  }
}

export interface SavingsData {
  time_saved: number
  value_saved: number
  since: string
}

export interface CreditsData {
  used: number // Total credits used in billing period (team total)
  total?: number // Total credits available (subscription_limit + purchased_credits)
  remaining?: number // Credits remaining in billing period (total - used)
  period_used: number // Credits used in selected time period (7d, 14d, etc.)
  subscription_limit?: number // Subscription credits limit (from license)
  purchased_credits?: number // Additional purchased credits (prepay system)
  personal_used?: number // Credits used by current user (personal usage only)
  billable_flows_limit?: number | null // Billable flows limit (null = unlimited)
  billable_flows_used?: number // Billable flows (tasks) used by current user
  team_billable_flows_used?: number // Billable flows (tasks) used by entire team
  /** Billable-flow billing model: customers never see credit totals; super admins see them with an on-card note. */
  is_billable_flow_plan?: boolean
}

export interface PaginationData {
  current_page: number
  total_pages: number
}

/**
 * Per-API-token usage row for the dashboard "API Keys" panel.
 * Backend aggregates MCP_CALL events (api_token_id from metric_metadata) and
 * LLM_TOKENS events (api_token_id derived via conversations.conversation_metadata).
 * Backend returns an empty array when no API token had activity in the window;
 * the frontend hides the panel in that case.
 */
export interface ApiKeyUsageEntry {
  id: string
  truncated_token: string
  scope: string
  tool_calls: number
  tasks: number
  tokens: number
  credits: number
  /** ISO8601 UTC, or null when only LLM activity exists and that branch had no rows. */
  last_used_at: string | null
}

export interface DashboardData {
  usage_overview: UsageOverviewData
  savings: SavingsData
  credits: CreditsData
  active_developers: number
  weekly_chart: ChartDataPoint[]
  /** ISO YYYY-MM-DD of the first `weekly_chart` bucket (UTC calendar); aligns expand with API window. */
  weekly_chart_starts_on?: string | null
  /** Backend bucket size: daily padding/expand only applies when `day`. */
  chart_bucket?: 'day' | 'week' | 'month'
  /** Inclusive YYYY-MM-DD (UTC calendar) matching the bar chart query window; aligns date UI. */
  usage_chart_starts_on?: string | null
  usage_chart_ends_on?: string | null
  leaderboard: LeaderboardEntry[]
  pagination: PaginationData
  /** Defaults to [] when backend omits the field (older deploys, error paths). */
  api_key_usage?: ApiKeyUsageEntry[]
}
